import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

/**
 * Cart “Save with Subscription” footer: convert OTP lines then checkout, or continue OTP to checkout.
 *
 * @typedef {Object} SubscribeFooterFrequency
 * @property {string} key
 * @property {string} label
 * @property {number} total_cents
 * @property {string} total_formatted
 *
 * @typedef {Object} SubscribeFooterLine
 * @property {string} key
 * @property {number} quantity
 * @property {Record<string, number>} plansByKey
 *
 * @typedef {Object} SubscribeFooterConfig
 * @property {string} checkoutUrl
 * @property {string | null} defaultFrequencyKey
 * @property {SubscribeFooterFrequency[]} frequencies
 * @property {SubscribeFooterLine[]} lines
 *
 * @typedef {Object} SubscribeFooterRefs
 * @property {HTMLScriptElement} config
 * @property {HTMLButtonElement} primaryButton
 * @property {HTMLSpanElement} primaryPrice
 * @property {HTMLSelectElement} [frequencySelect]
 * @property {HTMLButtonElement} secondaryButton
 *
 * @extends {Component<SubscribeFooterRefs>}
 */
class SvCartSubscribeFooter extends Component {
  requiredRefs = ['config', 'primaryButton', 'primaryPrice', 'secondaryButton'];

  /** @type {boolean} */
  #busy = false;

  /** @type {string | null} */
  #selectedFrequencyKey = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initFrequency();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initFrequency();
  }

  /** @returns {SubscribeFooterConfig} */
  #getConfig() {
    try {
      return JSON.parse(this.refs.config.textContent || '{}');
    } catch {
      return { checkoutUrl: '/checkout', defaultFrequencyKey: null, frequencies: [], lines: [] };
    }
  }

  #initFrequency() {
    const { defaultFrequencyKey, frequencies } = this.#getConfig();
    const select = this.refs.frequencySelect;

    if (select) {
      this.#selectedFrequencyKey = select.value || defaultFrequencyKey;
      this.#updatePrimaryPrice();
      return;
    }

    this.#selectedFrequencyKey = defaultFrequencyKey;
    if (this.#selectedFrequencyKey && Array.isArray(frequencies) && frequencies.length) {
      this.#updatePrimaryPrice();
    }
  }

  #updatePrimaryPrice() {
    const { frequencies } = this.#getConfig();
    const key = this.#selectedFrequencyKey;
    if (!key || !Array.isArray(frequencies)) return;

    const match = frequencies.find((frequency) => frequency.key === key);
    if (match?.total_formatted) {
      this.refs.primaryPrice.textContent = match.total_formatted;
    }
  }

  /** @returns {number | null} */
  #getSellingPlanForLine(/** @type {SubscribeFooterLine} */ line) {
    const key = this.#selectedFrequencyKey;
    if (key && line.plansByKey?.[key]) {
      return line.plansByKey[key];
    }

    const planIds = line.plansByKey ? Object.values(line.plansByKey) : [];
    return planIds.length ? planIds[0] : null;
  }

  #setBusy(busy) {
    this.#busy = busy;
    this.refs.primaryButton.disabled = busy;
    this.refs.secondaryButton.disabled = busy;
    if (this.refs.frequencySelect) {
      this.refs.frequencySelect.disabled = busy;
    }
    this.classList.toggle('is-busy', busy);
  }

  #goToCheckout() {
    const { checkoutUrl } = this.#getConfig();
    window.location.href = checkoutUrl || '/checkout';
  }

  /** @param {Event} [_event] */
  onFrequencyChange(_event) {
    if (this.#busy) return;
    const select = this.refs.frequencySelect;
    if (!select) return;

    this.#selectedFrequencyKey = select.value;
    this.#updatePrimaryPrice();
  }

  /** @param {Event} [_event] */
  onSecondaryClick(_event) {
    if (this.#busy) return;
    this.#goToCheckout();
  }

  /** @param {Event} [_event] */
  async onPrimaryClick(_event) {
    if (this.#busy) return;

    const { lines } = this.#getConfig();
    if (!Array.isArray(lines) || lines.length === 0) {
      this.#goToCheckout();
      return;
    }

    this.#setBusy(true);

    const changeUrl = Theme.routes.cart_change_url.endsWith('.js')
      ? Theme.routes.cart_change_url
      : `${Theme.routes.cart_change_url}.js`;

    try {
      for (const line of lines) {
        const sellingPlan = this.#getSellingPlanForLine(line);
        if (!sellingPlan) continue;

        const response = await fetch(
          changeUrl,
          fetchConfig('json', {
            body: JSON.stringify({
              id: line.key,
              quantity: line.quantity,
              selling_plan: sellingPlan,
            }),
          })
        );
        const data = await response.json();
        if (!response.ok || data.status || data.errors) {
          throw new Error(data.description || data.message || data.errors || 'Cart update failed');
        }
      }
      this.#goToCheckout();
    } catch (error) {
      console.error('[SvCartSubscribeFooter]', error);
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('sv-cart-subscribe-footer')) {
  customElements.define('sv-cart-subscribe-footer', SvCartSubscribeFooter);
}
